import React, { useState, useEffect } from 'react';
import {
  Document, Page, View, Text, StyleSheet, Image, pdf
} from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import logoUrl from '../assets/logo.png';

const BK = '#000000';
const BORDER = '#000000';

const s = StyleSheet.create({
  page: {
    paddingHorizontal: 30,
    paddingTop: 30,
    paddingBottom: 30,
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: BK,
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: BK,
    paddingBottom: 10,
  },
  logo: {
    width: 60,
    height: 60,
    objectFit: 'contain',
  },
  headerTextContainer: {
    flex: 1,
    marginLeft: 12,
  },
  title: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
  },
  subtitle: {
    fontSize: 10,
    marginTop: 2,
    color: '#444',
  },
  municipio: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'right',
  },
  section: {
    marginTop: 15,
    marginBottom: 5,
  },
  sectionTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    backgroundColor: '#f1f5f9',
    padding: 4,
    marginBottom: 8,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 8,
    gap: 15,
  },
  field: {
    flex: 1,
  },
  label: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: '#64748b',
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  value: {
    fontSize: 9,
    borderBottomWidth: 0.5,
    borderBottomColor: '#cbd5e1',
    paddingBottom: 2,
    minHeight: 12,
  },
  table: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: BORDER,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f8fafc',
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    alignItems: 'center',
    height: 20,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#e2e8f0',
    alignItems: 'center',
    minHeight: 20,
  },
  tableCellHeader: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    paddingHorizontal: 4,
  },
  tableCell: {
    fontSize: 8,
    paddingHorizontal: 4,
  },
  tableCellScientific: {
    fontSize: 8,
    paddingHorizontal: 4,
    fontFamily: 'Helvetica-Oblique',
  },
  col1: { width: '30%' },
  col2: { width: '20%' },
  col3: { width: '25%' },
  col4: { width: '25%' },
  positive: {
    color: '#ef4444',
    fontFamily: 'Helvetica-Bold',
  },
  negative: {
    color: '#10b981',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    borderTopWidth: 0.5,
    borderTopColor: '#94a3b8',
    paddingTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 7,
    color: '#94a3b8',
  }
});

// Utility para converter imagem em base64 e evitar travamentos de rede no PDF
const urlParaBase64 = (url) => {
  if (!url || typeof url !== 'string' || url.startsWith('data:')) return Promise.resolve(url);
  return new Promise((resolve) => {
    const el = document.createElement('img');
    el.crossOrigin = 'anonymous';
    el.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width  = el.naturalWidth;
        canvas.height = el.naturalHeight;
        const ctx = canvas.getContext('2d');
        // Usar PNG para preservar transparência
        ctx.drawImage(el, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) { resolve(null); }
    };
    el.onerror = () => resolve(null);
    el.src = url;
  });
};

const formatScientificName = (name) => {
  if (!name || name === '-') return '-';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    const genus = parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
    const species = parts[1].toLowerCase();
    const rest = parts.slice(2).join(' ');
    return `${genus} ${species}${rest ? ' ' + rest : ''}`;
  }
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

export const FormularioExamePDFDocument = ({ examData, fieldData, municipioNome, logoBase64 }) => {
  const exemplares = examData?.exemplares || [];
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr.replace(/-/g, '/'));
      return d.toLocaleDateString('pt-BR');
    } catch (e) { return dateStr; }
  };

  const enderecoFormatado = fieldData?.endereco 
    ? `${fieldData.endereco}${fieldData.numero ? ', ' + fieldData.numero : ''}` 
    : '-';

  return (
    <Document>
      <Page size="A4" style={s.page}>
        <View style={s.header}>
          <Image src={logoBase64 || logoUrl} style={s.logo} />
          <View style={s.headerTextContainer}>
            <Text style={s.title}>EXAME PARASITOLÓGICO DE TRIATOMÍNEOS</Text>
            <Text style={s.subtitle}>Para Controle da Doença de Chagas</Text>
          </View>
          <View style={{ width: 120 }}>
            <Text style={s.municipio}>{municipioNome || ''}</Text>
          </View>
        </View>

        <View style={s.section}>
          <Text style={s.sectionTitle}>Identificação da Amostra (Formulário de Campo)</Text>
          <View style={s.row}>
            <View style={[s.field, { flex: 0.5 }]}>
              <Text style={s.label}>Código de Etiqueta</Text>
              <Text style={s.value}>{fieldData?.codigo_etiqueta || '-'}</Text>
            </View>
            <View style={s.field}>
              <Text style={s.label}>Data da Atividade</Text>
              <Text style={s.value}>{formatDate(fieldData?.data_atividade)}</Text>
            </View>
            <View style={s.field}>
              <Text style={s.label}>Agente Responsável</Text>
              <Text style={s.value}>{fieldData?.agente_nome || '-'}</Text>
            </View>
          </View>
          <View style={s.row}>
            <View style={s.field}>
              <Text style={s.label}>Localidade</Text>
              <Text style={s.value}>{fieldData?.localidade || fieldData?.nome_localidade || '-'}</Text>
            </View>
            <View style={s.field}>
              <Text style={s.label}>Endereço</Text>
              <Text style={s.value}>{enderecoFormatado}</Text>
            </View>
          </View>
        </View>

        <View style={s.section}>
          <Text style={s.sectionTitle}>Dados do Exame</Text>
          <View style={s.row}>
            <View style={s.field}>
              <Text style={s.label}>Técnico Responsável</Text>
              <Text style={s.value}>{examData?.tecnico_responsavel || '-'}</Text>
            </View>
            <View style={s.field}>
              <Text style={s.label}>Data do Exame</Text>
              <Text style={s.value}>{formatDate(examData?.data_exame)}</Text>
            </View>
            <View style={s.field}>
              <Text style={s.label}>Situação do Laudo</Text>
              <Text style={s.value}>{examData?.situacao || '-'}</Text>
            </View>
          </View>
        </View>

        <View style={s.section}>
          <Text style={s.sectionTitle}>Exemplares Examinados</Text>
          <View style={s.table}>
            <View style={s.tableHeader}>
              <View style={[s.col1]}><Text style={s.tableCellHeader}>Espécie / Vetor</Text></View>
              <View style={[s.col2]}><Text style={s.tableCellHeader}>Captura</Text></View>
              <View style={[s.col3]}><Text style={s.tableCellHeader}>Estágio</Text></View>
              <View style={[s.col4]}><Text style={s.tableCellHeader}>Resultado</Text></View>
            </View>
            
            {exemplares.length === 0 ? (
              <View style={s.tableRow}>
                <Text style={[s.tableCell, { flex: 1, textAlign: 'center', color: '#94a3b8' }]}>Nenhum exemplar registrado</Text>
              </View>
            ) : (
              exemplares.map((ex, i) => (
                <View key={i} style={s.tableRow}>
                  <View style={s.col1}><Text style={s.tableCellScientific}>{formatScientificName(ex.vetor)}</Text></View>
                  <View style={s.col2}><Text style={s.tableCell}>{ex.captura || '-'}</Text></View>
                  <View style={s.col3}><Text style={s.tableCell}>{ex.estagio || '-'}</Text></View>
                  <View style={s.col4}>
                    <Text style={[
                      s.tableCell, 
                      ex.resultado?.includes('Positivo') ? s.positive : 
                      ex.resultado?.includes('Negativo') ? s.negative : {}
                    ]}>
                      {ex.resultado || '-'}
                    </Text>
                  </View>
                </View>
              ))
            )}
          </View>
        </View>

        <View style={s.footer}>
          <Text style={s.footerText}>Vector Tracker - Sistema de Vigilância Entomológica</Text>
          <Text style={s.footerText}>Gerado em: {new Date().toLocaleString('pt-BR')}</Text>
        </View>
      </Page>
    </Document>
  );
};

export const BotaoDownloadExamePDF = ({ examData, fieldData, municipioNome }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [logoBase64, setLogoBase64] = useState(null);

  // Pré-carrega o logo para evitar delays na geração do PDF
  useEffect(() => {
    urlParaBase64(logoUrl).then(setLogoBase64);
  }, []);

  const handleDownload = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      const doc = (
        <FormularioExamePDFDocument 
          examData={examData} 
          fieldData={fieldData} 
          municipioNome={municipioNome}
          logoBase64={logoBase64}
        />
      );
      
      const blob = await pdf(doc).toBlob();
      const nomeArquivo = `Laudo_${fieldData?.codigo_etiqueta || 'SemEtiqueta'}.pdf`;
      saveAs(blob, nomeArquivo);
    } catch (err) {
      console.error("Erro ao gerar PDF:", err);
      alert("Erro ao gerar o laudo. Tente novamente.");
    } finally {
      setIsGenerating(false);
    }
  };

  const btnBase = {
    display: 'inline-flex', alignItems: 'center', gap: '7px',
    padding: '6px 14px', color: '#fff', border: 'none',
    borderRadius: '6px', fontWeight: '600', fontSize: '12px',
    fontFamily: "'Inter', sans-serif",
    height: '32px',
    background: isGenerating ? '#6b7280' : '#111827',
    cursor: isGenerating ? 'wait' : 'pointer',
    transition: 'background 0.2s'
  };

  return (
    <button 
      style={btnBase} 
      onClick={handleDownload} 
      disabled={isGenerating}
      title="Baixar Laudo PDF"
    >
      <DownloadIcon />
      {isGenerating ? 'Gerando...' : 'Baixar Laudo PDF'}
    </button>
  );
};

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
